import React, { useState, useEffect } from "react";
import { Download, Eye, CheckCircle, Home, Info, Lock } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { imgNbaLogo } from "../../../assets";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { InlineLoader } from "../../../components/Loader/LoadingOverlay";
import { ButtonLoader } from "../../../components/Loader";

interface CertificateData {
  certificateId: string;
  transactionType: string;
  propertyFingerprint: string;
  address: string;
  gpsCoordinates: string;
  propertyType: string;
  numberOfUnits: string;
  builtArea: string;
  landArea: string;
  legalPractitioner: string;
  nbaBranch: string;
  client: string;
  complianceStatus: string;
  dateOfIssuance: string;
  qrCodeUrl: string;
}

export default function Certificate() {
  const { certID } = useParams<{ certID: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [certificateData, setCertificateData] = useState<CertificateData | null>(null);

  useEffect(() => {
    if (certID) {
      fetchCertificate();
    }
  }, [certID]);

  async function fetchCertificate() {
    if (!certID) return;

    setIsLoading(true);
    try {
      const endpoint = Endpoints.GetCertificate(certID);
      const response = await PerformRequest<CertificateData>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        setCertificateData(response.data);
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to load certificate. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: "Failed to load certificate. Please try again.",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleDownload = async () => {
    if (!certID) return;

    try {
      const endpoint = Endpoints.GetTransactionCertificate(certID);
      const response = await PerformRequest<Blob>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `certificate-${certID}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        notifications.show({
          title: "Success",
          message: "Certificate downloaded successfully!",
          color: "green",
        });
      } else {
        notifications.show({
          title: "Error",
          message: response.message || "Failed to download certificate. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error?.message || "Failed to download certificate. Please try again.",
        color: "red",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="certificate-page">
        <InlineLoader isLoading={true} text="Loading certificate..." size="lg" />
      </div>
    );
  }

  if (!certificateData) {
    return (
      <div className="certificate-page">
        <div className="certificate-empty">Certificate not found</div>
      </div>
    );
  }

  // Use fetched data or fallback to defaults
  const displayData = certificateData || {
    certificateId: certID || "NBA-CERT-2024-001",
    transactionType: "Multi-Unit Property Purchase",
    propertyFingerprint:
      "a7f3c9e2b8d4f6a1c5e8b9d2f4a6c8e1b3d5f7a9c2e4b6d8f1a3c5e7b9d2f4a6",
    address: "15 Admiralty Way, Lekki Phase 1, Lagos State",
    gpsCoordinates: "6.443* N, 3.5106* E Verified GPS detection",
    propertyType: "Residential Duplex",
    numberOfUnits: "2 Units",
    builtArea:
      "4 bedroom duplex with ensuite bathrooms, spacious living room, modern kitchen, BQ, and secure parking",
    landArea: "650 square meters",
    legalPractitioner: "Barrister Adebayo Ogunleye SCN: 45678/2010",
    nbaBranch: "Lagos Branch",
    client: "Mr. Chukwuma Okafor Client ID: CLT-8F3A9D2E",
    complianceStatus: "✓ Completed All requirements satisfied",
    dateOfIssuance: "December 15, 2024",
    qrCodeUrl: "#",
  };

  return (
    <div className="certificate-page">
      <div className="certificate-container">
        {/* Header Section */}
        <div className="certificate-header">
          <div className="certificate-logo-section">
            <img
              className="certificate-logo-img"
              src={imgNbaLogo}
              alt="NBA Logo"
            />
            <div className="certificate-logo-text">
              <div className="certificate-logo-name">
                NIGERIAN BAR ASSOCIATION
              </div>
              <div className="certificate-logo-sub">REMUNERATION PORTAL</div>
            </div>
          </div>
          <h1 className="certificate-main-title">COMPLIANCE CERTIFICATE</h1>
        </div>

        {/* Certificate ID Section */}
        <div className="certificate-id-section">
          <div className="certificate-id-content">
            <div className="certificate-id-label">CERTIFICATE ID</div>
            <div className="certificate-id-value">
              {displayData.certificateId}
            </div>
          </div>
          <div className="certificate-status-badge">
            <span>TRANSACTION COMPLETED</span>
          </div>
        </div>

        {/* Transaction Type */}
        <div className="certificate-section">
          <div className="certificate-section-label">TRANSACTION TYPE</div>
          <div className="certificate-section-value">
            {displayData.transactionType}
          </div>
        </div>

        {/* Property Fingerprint */}
        <div className="certificate-section certificate-section--fingerprint">
          <div className="certificate-section-label">PROPERTY FINGERPRINT</div>
          <div className="certificate-fingerprint">
            {displayData.propertyFingerprint}
          </div>
        </div>

        {/* Property Details */}
        <div className="certificate-section">
          <div className="certificate-section-header">
            <Home className="certificate-section-icon" size={30} />
            <h2 className="certificate-section-title">PROPERTY DETAILS</h2>
          </div>

          <div className="certificate-details-grid">
            <div className="certificate-detail-item">
              <div className="certificate-detail-label">ADDRESS</div>
              <div className="certificate-detail-value">
                {displayData.address}
              </div>
            </div>

            <div className="certificate-detail-item">
              <div className="certificate-detail-label">GPS COORDINATES</div>
              <div className="certificate-detail-value">
                {displayData.gpsCoordinates}
              </div>
            </div>

            <div className="certificate-detail-item">
              <div className="certificate-detail-label">PROPERTY TYPE</div>
              <div className="certificate-detail-value">
                {displayData.propertyType}
              </div>
            </div>

            <div className="certificate-detail-item">
              <div className="certificate-detail-label">NUMBER OF UNITS</div>
              <div className="certificate-detail-value">
                {displayData.numberOfUnits}
              </div>
            </div>

            <div className="certificate-detail-item certificate-detail-item--full">
              <div className="certificate-detail-label">BUILT AREA</div>
              <div className="certificate-detail-value">
                {displayData.builtArea}
              </div>
            </div>

            <div className="certificate-detail-item">
              <div className="certificate-detail-label">LAND AREA</div>
              <div className="certificate-detail-value">
                {displayData.landArea}
              </div>
            </div>
          </div>
        </div>

        {/* Legal and Administrative */}
        <div className="certificate-section">
          <h2 className="certificate-section-title">
            LEGAL AND ADMINISTRATIVE
          </h2>

          <div className="certificate-details-grid">
            <div className="certificate-detail-item">
              <div className="certificate-detail-label">LEGAL PRACTITIONER</div>
              <div className="certificate-detail-value">
                {displayData.legalPractitioner}
              </div>
            </div>

            <div className="certificate-detail-item">
              <div className="certificate-detail-label">NBA BRANCH</div>
              <div className="certificate-detail-value">
                {displayData.nbaBranch}
              </div>
            </div>

            <div className="certificate-detail-item">
              <div className="certificate-detail-label">CLIENT</div>
              <div className="certificate-detail-value">
                {displayData.client}
              </div>
            </div>

            <div className="certificate-detail-item">
              <div className="certificate-detail-label">COMPLIANCE STATUS</div>
              <div className="certificate-detail-value">
                {displayData.complianceStatus}
              </div>
            </div>
          </div>
        </div>

        {/* Date of Issuance and QR Code */}
        <div className="certificate-section">
          <div className="certificate-date-section">
            <div className="certificate-date-label">DATE OF ISSURANCE</div>
            <div className="certificate-date-value">
              {displayData.dateOfIssuance}
            </div>
          </div>

          <div className="certificate-qr-section">
            <h3 className="certificate-qr-title">ONE-TIME VERIFICATION QR CODE</h3>
            <div className="certificate-qr-code">
              <div className="certificate-qr-placeholder">
                <div className="certificate-qr-grid">
                  {/* QR Code placeholder - in real app, use a QR code library */}
                  <div className="certificate-qr-pattern" />
                </div>
              </div>
            </div>
            <p className="certificate-qr-note">
              Scan to verify certificate authenticity. Valid for single
              verification only.
            </p>
          </div>
        </div>

        {/* Certificate Usage Info */}
        <div className="certificate-section certificate-section--info">
          <div className="certificate-info-item">
            <Info size={30} />
            <p className="certificate-info-text">
              This certificate is valid for government registrations, court
              proceedings, and official land registry submissions.
            </p>
          </div>

          <div className="certificate-info-item">
            <Lock size={30} />
            <p className="certificate-info-text">
              Confidential: Financial transaction details are accessible to NBA
              administrators only and are not displayed on this public
              certificate.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="certificate-footer">
          <p className="certificate-footer-text">
            Digitally issued by Nigerian Bar Association, Lagos State
            Remuneration Compliance Platform. Verify at nba-lagos.verify.ng
          </p>
        </div>

        {/* Action Buttons */}
        <div className="certificate-actions">
          <button
            className="dashboard-button dashboard-button--primary dashboard-button--large"
            type="button"
            onClick={handleDownload}
            disabled={isLoading}
          >
            <Download size={24} />
            <span>DOWNLOAD COMPLIANCE CERTIFICATE</span>
          </button>
          <button
            className="dashboard-button dashboard-button--secondary dashboard-button--large"
            type="button"
          >
            <Eye size={20} />
            <span>Preview Certificate</span>
          </button>
        </div>
      </div>
    </div>
  );
}

